import { Component, OnInit, HostBinding } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { EmailValidator } from './emailValidator';
import { AuthConfigService } from '../../services/auth-config/auth-config.service';
import { InscriptionService } from '../../services/inscription/inscription.service';

import { formatDate } from '@telerik/kendo-intl';

@Component({
  selector: 'ags-inscription',
  templateUrl: './inscription.component.html',
  styleUrls: ['./inscription.component.scss']
})
export class InscriptionComponent implements OnInit {
  @HostBinding('class') themeFlag: string;

  formSignup: UntypedFormGroup;
  client: string;
  email: string;
  confirmEmail: string;

  listEntreprise = null;
  listParam = null;

  signupError = '';
  homonymeP = false;
  homonymeE = false;

  public dataToSuccess: string;
  public successInscription: boolean;
  public isRequesting: boolean;

  // types civilités
  public listCiv: Array<string> = ['M.', 'Mme'];

  // type clients
  public listClient: Array<string> = ['une entreprise / organisation', 'un particulier / indépendant'];

  constructor(
    private authConfig: AuthConfigService,
    private inscriptionService: InscriptionService,
    private formBuilder: UntypedFormBuilder
  ) {
    this.themeFlag = `${this.themeFlag || ''} ${authConfig.themingClassName}`;
    // initialisation de la page sur type : entreprise
    this.client = 'une entreprise / organisation';

    // champs formulaire création pour une entreprise
    this.formSignup = this.formBuilder.group({
      typeEntreprise: [null, Validators.required],
      raisonSociale: [null, Validators.required],
      adresse: [null, Validators.required],
      code: [null],
      ville: [null, Validators.required],
      civ: [null],
      email: [null, [Validators.required, Validators.email]],
      confirmEmail: [null, [Validators.required, Validators.email, EmailValidator('email')]],
      nom: [null, Validators.required],
      prenom: [null, Validators.required],
      tva: [null],
      siret: [null]
    });

    // récupère tous les types d'entreprise en base (table type_client)
    this.inscriptionService.getEcomParams().subscribe((list: any) => {
      if (list && list.params) {
        this.listEntreprise = (list.params.listTypeClients || []).filter(
          // enlève "particulier" de la liste
          type => /[^Particulier à titre individuel]/gi.test(type.libelle)
        );
        // met par ordre alphabétique
        this.listEntreprise.sort((a, b) => (a.libelle > b.libelle ? 1 : b.libelle > a.libelle ? -1 : 0));

        // récupère les paramètres client rentrés en base (table site_ecom_params)
        this.listParam = list.params.listEcomParams || [];
      }
    });
  }

  ngOnInit() {}

  // permet de récupérer la bonne information (car table créée en code / valeur)
  getParamByCode(code) {
    if (!this.listParam || !this.listParam.length) {
      return {};
    }
    return this.listParam.find(param => param.code === code);
  }

  // gérer 2 forms différents selon type de client
  clientSelected(value) {
    this.homonymeE = false;
    this.homonymeP = false;
    this.signupError = null;
    // pour une entreprise
    if (value === 'une entreprise / organisation') {
      this.formSignup = this.formBuilder.group({
        typeEntreprise: [null, Validators.required],
        raisonSociale: [null, Validators.required],
        adresse: [null, Validators.required],
        code: [null],
        ville: [null, Validators.required],
        civ: [null],
        email: [null, [Validators.required, Validators.email]],
        confirmEmail: [null, [Validators.required, Validators.email, EmailValidator('email')]],
        nom: [null, Validators.required],
        prenom: [null, Validators.required],
        // champs nécessaires en cas de double vérification
        tva: [null],
        siret: [null]
      });
      // pour un particulier
    } else {
      this.formSignup = this.formBuilder.group({
        email: [null, [Validators.required, Validators.email]],
        confirmEmail: [null, [Validators.required, Validators.email, EmailValidator('email')]],
        civ: [null],
        nom: [null, Validators.required],
        prenom: [null, Validators.required],
        // champs nécessaires en cas de double vérification
        adresseP: [null],
        codeP: [null],
        villeP: [null],
        date: [null]
      });
    }
  }

  signUpCheck() {
    // contient les infos à envoyer dans le back Argalis
    const info = this.formSignup.value;
    // on ne prend que le libelle du type d'entreprise (pas réussi à dire à Kendo de le faire)
    if (info && info.typeEntreprise && typeof info.typeEntreprise !== 'string') {
      info.typeEntreprise = info.typeEntreprise.libelle;
    }
    // réinitialise message d'erreur dans html
    this.signupError = '';
    // formate la civilité
    this.formSignup.value.civ = this.formSignup.value.civ === 'Mme' ? 'madame' : 'monsieur';

    // on grise tout jusqu'à ce que le serveur réponde
    this.isRequesting = true;
    // si particulier
    if (this.client === 'un particulier / indépendant') {
      // formate date
      if (this.formSignup.value.date) {
        this.formSignup.value.date = formatDate(new Date(this.formSignup.value.date), 'u');
        this.formSignup.value.date = this.formSignup.value.date.substring(0, 11);
      }

      // on va chercher en base s'il y a doublon (selon plusieurs critères)
      this.inscriptionService.signUpParticulier(info).subscribe(
        (res: any) => {
          this.isRequesting = false;
          if (!res || !res.data) this.signupError = `Un problème est survenue, merci de revenir plus tard`;
          switch (res.data) {
            // si email déjà en base
            case 'matchMail':
              this.signupError = `Il existe déjà un compte utilisant le même identifiant : ${info.email}.
            <br>Veuillez vous connecter avec ce compte ou renseigner une autre adresse mail.`;
              // vide les champs mail
              this.email = '';
              this.confirmEmail = '';
              break;

            // si nom/prénom déjà en base
            case 'matchName':
              this.homonymeP = true;
              this.signupError = `Un homonyme est déjà présent, afin de vous identifier veuillez renseigner les champs suivants :`;
              // passe les champs qui viennent d'apparaitre dans HTML en required
              this.formSignup.controls['adresseP'].setValidators([Validators.required]);
              this.formSignup.controls['codeP'].setValidators([Validators.required]);
              this.formSignup.controls['villeP'].setValidators([Validators.required]);
              this.formSignup.controls['date'].setValidators([Validators.required]);
              break;

            // si confirmation que c'est un doublon en base
            case 'matchClientAll':
              // TODO : mettre dans une pop-up qui ramène à home page après clic sur "OK"
              this.signupError = `Un autre client dispose déjà d'information similaires,
            <br>la création d'un compte client sur notre site est impossible.
            <br>Veuillez prendre contact avec notre service client.`;
              break;

            case 'OK':
              this.dataToSuccess = info.email;
              this.signupError = ``;
              this.successInscription = true;
              break;

            default:
              this.signupError = `Une erreur est survenue`;
          }
        },
        () => {
          this.isRequesting = false;
          this.signupError = `Nous avons rencontré une erreur. Veuillez prendre contact avec notre service client`;
        }
      );
      // si entreprise
    } else if (this.client === 'une entreprise / organisation') {
      this.inscriptionService.signUpEntreprise(info).subscribe(
        (res: any) => {
          this.isRequesting = false;
          if (!res || !res.data) this.signupError = `Un problème est survenue, merci de revenir plus tard`;
          switch (res.data) {
            case 'matchMail':
              this.signupError = `Il existe déjà un compte utilisant le même identifiant : ${info.email}.
            <br>Veuillez vous connecter avec ce compte ou renseigner une autre adresse mail.`;
              // vide les champs mail
              this.email = '';
              this.confirmEmail = '';
              break;
            case 'matchEntreprise':
              this.homonymeE = true;
              this.signupError = `Une organisation similaire est déjà présente, afin de bien l'identifier veuillez renseigner les champs suivants :`;

              // passe les champs qui viennent d'apparaitre dans HTML en required
              this.formSignup.controls['tva'].setValidators([Validators.required]);
              this.formSignup.controls['siret'].setValidators([Validators.required]);
              break;

            case 'matchEntrepriseAll':
              // TODO : mettre dans une pop-up qui ramène à home page après clic sur "OK"
              this.signupError = `Une organisation identique est déjà présente, la création d'un compte client
            sur notre site est impossible.<br>Veuillez prendre contact avec notre service client`;
              break;

            case 'OK':
              this.dataToSuccess = info.email;
              this.signupError = ``;
              this.successInscription = true;
              break;

            default:
              this.signupError = `Une erreur est survenue`;
          }
        },
        () => {
          this.isRequesting = false;
          this.signupError = `Nous avons rencontré une erreur. Veuillez prendre contact avec notre service client`;
        }
      );
    }
  }
}
